<script setup lang="ts">
import { ref, useAttrs, watch } from 'vue'
import { useAttachmentUpload } from '@/composables/business/useAttachmentUpload'

defineOptions({
  name: 'AppAttachmentImageUpload',
  inheritAttrs: false,
})

const props = withDefaults(defineProps<{
  modelValue?: string[] | string
  method?: string
  data?: Record<string, any>
  name?: string
  multiple?: boolean
  ext?: string[]
  max?: number
  width?: number
  height?: number
  dimension?: {
    width: number
    height: number
  }
  size?: number
  hideTips?: boolean
  disabled?: boolean
}>(), {
  modelValue: () => [],
  method: 'post',
  data: () => ({}),
  name: 'file',
  multiple: false,
  ext: () => [],
  max: 1,
  width: 100,
  height: 100,
  size: 5 * 1024 * 1024,
  hideTips: false,
  disabled: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: string[] | string]
  'onSuccess': [response: any, file: File]
}>()

const attrs = useAttrs()
const { uploadAction, uploadHeaders, normalizeUploadUrl } = useAttachmentUpload()
const innerValue = ref<string[]>([])

function isSameStringArray(left: string[], right: string[]) {
  return left.length === right.length && left.every((item, index) => item === right[index])
}

watch(
  () => props.modelValue,
  (value) => {
    const nextValue = Array.isArray(value) ? [...value] : (value ? [value] : [])
    if (isSameStringArray(innerValue.value, nextValue)) {
      return
    }
    if (Array.isArray(value)) {
      innerValue.value = [...value]
      return
    }
    innerValue.value = value ? [value] : []
  },
  { immediate: true, deep: true },
)

watch(
  innerValue,
  (value) => {
    const shouldEmitArray = Array.isArray(props.modelValue) || props.multiple || props.max !== 1
    if (shouldEmitArray) {
      const currentValue = Array.isArray(props.modelValue) ? props.modelValue : (props.modelValue ? [props.modelValue] : [])
      if (isSameStringArray(currentValue, value)) {
        return
      }
      emit('update:modelValue', [...value])
      return
    }

    const nextValue = value[0] ?? ''
    if ((props.modelValue ?? '') === nextValue) {
      return
    }
    emit('update:modelValue', nextValue)
  },
  { deep: true },
)

function handleSuccess(response: any, file: File) {
  emit('onSuccess', response, file)
}
</script>

<template>
  <FaImageUpload
    v-bind="attrs"
    v-model="innerValue"
    :action="uploadAction"
    :headers="uploadHeaders"
    :method="props.method"
    :data="props.data"
    :name="props.name"
    :multiple="props.multiple"
    :ext="props.ext"
    :max="props.max"
    :width="props.width"
    :height="props.height"
    :dimension="props.dimension"
    :size="props.size"
    :hide-tips="props.hideTips"
    :disabled="props.disabled"
    :after-upload="normalizeUploadUrl"
    @on-success="handleSuccess"
  />
</template>
